import { SplitFactory } from '@splitsoftware/splitio-react';
import { parse } from 'qs';
import React, { useEffect, useMemo } from 'react';
import { Router, useLocation } from 'react-router-dom';
import AssemblyLanderLoadingScreen from '../atomic/molecules/AssemblyLanderLoadingScreen';
import RoutesList from '../routesList';
import { APP_URL, SPLIT_AUTH_KEY } from '../config';
import { DEVICE, DEVICES } from '../constants/devices';
import history from '../history';
import useDataDog from '../hooks/useDataDog';

import { HttpsStatus } from '../interfaces/ResponseError';
import {
  GetProfileInfoResponse,
  useProfileInfoFetchQuery,
} from '../queries/Profile';
import { trackPageView } from '../Utils/analytics';
import { setSourceDeviceAsIPhone } from '../Utils/device';
import KioskFooter from '../atomic/organism/KioskFooter';
import MultiAssemblyRouters from './multiAssemblyRouters';
import {
  EXTENSION_LOADING_PATH,
  TEMPLATE,
  USER_ONBOARDING_WITHOUT_OPTIONS_SCREEN,
} from '../constants/routes';
import isEmpty from 'lodash/isEmpty';
import { microsoftTeamsStore } from '../stores/microsoftStore';
import { authStore } from '../stores/authStore';
import { useMixPanelInitialization } from '../hooks/useMixPanelInitialization';
import { datadogRum } from '@datadog/browser-rum';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '../atomic/pages/ErrorFallback';
import { useDataDogRecording } from '../hooks/useDataDogRecording';
import useAdminSettingsUpdatedSocket from '../hooks/useAdminSettingsUpdatedSocket';

const ANONYMOUS = 'anonymous';

class ApplicationCrashError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ApplicationCrashError';
  }
}

const SplitV2 = ({
  shortCode,
  slugUrl,
  data,
}: {
  slugUrl: string;
  shortCode?: string;
  data: GetProfileInfoResponse | undefined;
}) => {
  useDataDogRecording();
  const { search, pathname } = useLocation();

  const slugFromUrl = pathname.split('/')[1];
  const slugCodeFromUrl = slugFromUrl.split('-');
  const slugFromApi = slugUrl.split('-');
  const slugCodeFromApi = slugFromApi[slugFromApi.length - 1];

  const REDIRECT_ROUTES = [
    `${window.location.origin}/home`,
    `${window.location.origin}/settings`,
    `${window.location.origin}/rewards`,
    `${window.location.origin}/notebook`,
    `${window.location.origin}/knowledge-center`,
    `${window.location.origin}/flows`,
    `${window.location.origin}/post`,
    `${window.location.origin}/user`,
    `${window.location.origin}/template/`,
    `${window.location.origin}/create-one-click-flow/`,
  ];

  /*
  There are few conflicts routes where slugUrl is not needed to be appended,
  to handle that we're using this array
  */
  const NO_REDIRECT_ROUTES = [
    `/user/set-details`,
    '/user/account-setup',
    USER_ONBOARDING_WITHOUT_OPTIONS_SCREEN,
  ];

  const WORKSPACE_ID = shortCode || '';

  const WORKSPACE_KEY = 'WORKSPACE_KEY';

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      const externalPaths = [TEMPLATE];
      const currentPath = window.location.pathname;
      if (!externalPaths.some((path) => currentPath.includes(path))) {
        const isUserLoggedInToAnotherAssembly =
          e.key === WORKSPACE_KEY &&
          !isEmpty(e.newValue) &&
          !isEmpty(WORKSPACE_ID) &&
          WORKSPACE_ID !== e.newValue;
        if (isUserLoggedInToAnotherAssembly) {
          window.location.href = '/404';
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    window.localStorage.setItem(WORKSPACE_KEY, WORKSPACE_ID);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [WORKSPACE_ID]);

  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  useEffect(() => {
    const fromMSTeams = Boolean(
      new URLSearchParams(search).get('fromMSTeams') || false,
    );
    if (fromMSTeams) {
      microsoftTeamsStore.getState().setIsLoggedInThroughNewTeamsApp(true);
    }
  }, [search]);

  useEffect(() => {
    const parsedParams = parse(search, {
      ignoreQueryPrefix: true,
    });
    const isDevicePresent = parsedParams && parsedParams[DEVICE];
    if (isDevicePresent) {
      const { device } = parsedParams;
      if (device === DEVICES.IPHONE) {
        setSourceDeviceAsIPhone();
      }
    }
  }, [search]);
  if (data?.assembly) {
    if (
      !window.location.href.includes(`${window.location.origin}/admin`) &&
      !window.location.href.includes(shortCode || '') &&
      REDIRECT_ROUTES.some((route) => window.location.href.includes(route)) &&
      !NO_REDIRECT_ROUTES.some((route) =>
        window.location.pathname.startsWith(route),
      )
    ) {
      history.push(`/${slugUrl}${pathname}${search}`);
      return null;
    } else if (
      slugCodeFromApi === slugCodeFromUrl[slugCodeFromUrl.length - 1] &&
      slugFromUrl !== slugUrl
    ) {
      history.replace(
        `${window.location.pathname.replace(slugFromUrl, slugUrl)}${search}`,
      );
    } else if (
      window.location.href === `${APP_URL}${slugUrl}/` ||
      window.location.href === `${APP_URL}${slugUrl}`
    ) {
      history.replace(
        `${window.location.pathname.replace(`${slugFromUrl}/`, slugUrl)}/home`,
      );
    }
  }

  return <MultiAssemblyRouters slugUrl={slugUrl} />;
};

const Routers = () => {
  const authenticated = authStore.getState().authenticated;
  useDataDog();
  useMixPanelInitialization();
  useAdminSettingsUpdatedSocket();

  const { data, isLoading, isError, error } =
    useProfileInfoFetchQuery(authenticated);

  const shortCode = data?.assembly.workspaceSlug.shortCode;
  const slugUrl = `${data?.assembly.workspaceSlug.name}-${shortCode}`;

  const splitConfig = useMemo(() => {
    const assemblyId = data?.assembly?.assemblyId;
    return {
      core: {
        authorizationKey: SPLIT_AUTH_KEY,
        key: assemblyId || ANONYMOUS,
      },
      startup: {
        readyTimeout: 1.5, // 1.5 sec
      },
    };
  }, [data]);

  useEffect(() => {
    const externalPaths = [TEMPLATE];
    const currentPath = window.location.pathname;
    if (
      isError &&
      error &&
      error.response &&
      error.response.status === HttpsStatus.UNAUTHORIZED &&
      !externalPaths.some((path) => currentPath.includes(path))
    ) {
      history.push(RoutesList.SIGN_OUT);
    }
  }, [error, isError]);

  const showAltLoader = window.location.pathname === EXTENSION_LOADING_PATH;
  if (isLoading && authenticated && !showAltLoader) {
    return <AssemblyLanderLoadingScreen />;
  }

  return (
    <SplitFactory config={splitConfig} updateOnSdkUpdate>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(
          err: Error,
          info: {
            componentStack: string;
          },
        ) => {
          datadogRum.addError(
            new ApplicationCrashError(JSON.stringify(err)),
            info,
          );
        }}
      >
        <Router history={history}>
          <SplitV2 slugUrl={slugUrl} shortCode={shortCode} data={data} />
          <KioskFooter />
        </Router>
      </ErrorBoundary>
    </SplitFactory>
  );
};
export default Routers;
